<template>
    <div class="user-modal-login-view px-2">
        <Transition name="fade-grow" appear>
            <div v-if="emailSentText" class="py-3 px-4 text-white bg-info mb-5 rounded">
                <p class="mb-0" v-html="emailSentText"></p>
            </div>
        </Transition>

        <div class="mb-5 text-center">
            <p class="font-weight-bold font-size-200 mb-0">{{ translate('Log In') }}</p>
        </div>

        <SocialLoginButtons class="mb-4 w-100 text-center d-flex flex-column gap-10"></SocialLoginButtons>

        <div class="mb-4 text-center text-lowercase">
            <strong>{{ translate('Or') }}</strong>
        </div>

        <div v-if="showLoginWrongCredentialsError" class="text-danger font-weight-bold text-center mt-4 mb-3">
            {{ translate('Incorrect username or password.') }}
        </div>

        <div class="mb-5 d-flex flex-column gap-10" @keyup.enter="attemptLogin">
            <TextInput
                data-user-modal-input-email
                type="email"
                name="u_email"
                :label="translate('E-Mail or Username')"
                icon="far fa-envelope"
                @input="updateUsernameOrEmail"
                :disabled="isSubmitting"
                :has-error="showLoginWrongCredentialsError || usernameOrEmailError !== null"
                :current-error="usernameOrEmailError"
                :trim-input="true"
                autocomplete="on"
            ></TextInput>

            <TextInput
                data-user-modal-input-password
                type="password"
                name="u_password"
                :label="translate('Password')"
                icon="far fa-lock"
                @input="updatePassword"
                :disabled="isSubmitting"
                :has-error="showLoginWrongCredentialsError || passwordError !== null"
                :current-error="passwordError"
                :trim-input="false"
                autocomplete="current-password"
            ></TextInput>
        </div>

        <ButtonWithLoadingState
            data-user-modal-button-submit
            class="btn btn-success btn-lg btn-block font-weight-bold text-uppercase rounded mb-4"
            icon="fa fa-sign-in"
            :text="translate('Log in')"
            :is-state-loading="isSubmitting"
            @click.stop.prevent="attemptLogin"
        ></ButtonWithLoadingState>

        <div class="text-center">
            {{ translate('Having trouble?') }}
            <a data-user-modal-link-help href="" @click.stop.prevent="goToHelpPage">{{ translate('Get help') }}</a>
        </div>

        <div class="text-center mt-2">
            <RouterLink
                data-user-modal-link-reset-password
                :to="{ name: userModalRoute.PASSWORD_RESET }"
                :disabled="isSubmitting"
                :event="isSubmitting ? '' : 'click'"
                tag="a"
                >{{ translate('Forgot your password?') }}
            </RouterLink>
        </div>

        <hr class="mb-4 mt-4" />

        <div class="text-center">
            {{ translate("Don't have an account yet?") }}
            <RouterLink
                data-user-modal-link-register
                :to="{ name: userModalRoute.REGISTER }"
                :disabled="isSubmitting"
                :event="isSubmitting ? '' : 'click'"
                tag="a"
                >{{ translate('Sign up') }}
            </RouterLink>
        </div>
    </div>
</template>

<script>
import TransFilter from '../../../../../qaamgo/modules/sat/components/mixins/TransFilter';
import TextInput from '../../../../../qaamgo/modules/sat/components/form/TextInput';
import ButtonWithLoadingState from '../../../../../qaamgo/modules/sat/components/form/ButtonWithLoadingState';
import { pageStoreMixin } from '../../../../store/page-store';
import {
    testIsEmptyString,
    testIsNonEmptyString,
    testIsNotNull,
    testIsTrimmedStringEmpty,
} from '../../../../../lib/test-and-assert/test-base';
import { translate } from '../../../../../qaamgo/helper/translate';
import { pageNotification } from '../../../../../qaamgo/helper/notify';
import { User } from '../../../user';
import { userModalRoute } from '../router';
import { userManager } from '../../../user-manager';
import { LOGIN_FAIL_INVALID_DATA, LOGIN_FAIL_NOT_ENABLED } from '../../../user-api';
import { satGlobals } from '../../../../../qaamgo/sat/helper/sat-globals';
import { translatePath } from '../../../../../qaamgo/sat/helper/translate-path';
import { globalLogger } from '../../../../../qaamgo/helper/global-logger';
import SocialLoginButtons from '../components/SocialLoginButtons';
import { getLocationHashWithoutModalPageFragment } from '../modal-fragment-helper';

export default {
    name: 'LoginView',
    components: { SocialLoginButtons, ButtonWithLoadingState, TextInput },
    mixins: [TransFilter, pageStoreMixin],
    props: {
        emailSentText: {
            type: String,
            default: null,
        },
    },
    computed: {
        /** @return {Boolean} */
        isSubmitting() {
            return this.store.userPaymentModal.isSubmitting;
        },
    },
    methods: {
        goToHelpPage() {
            const link = satGlobals.getMainUrl() + translatePath('/help');

            window.open(link, '_blank');
        },
        updateUsernameOrEmail(value) {
            this.usernameOrEmailValue = value;

            this.usernameOrEmailError = null;
        },
        updatePassword(value) {
            this.passwordValue = value;

            this.passwordError = null;
        },
        validateEmailAndSetErrors() {
            this.usernameOrEmailError = null;

            if (testIsTrimmedStringEmpty(this.usernameOrEmailValue)) {
                this.usernameOrEmailError = translate('Enter your username or e-mail address');
            }
        },
        validatePasswordAndSetErrors() {
            this.passwordError = null;

            if (testIsEmptyString(this.passwordValue)) {
                this.passwordError = translate('Enter your password');
            }

            if (this.passwordValue.length > 256) {
                this.passwordError = translate('Password can not be greater than %amount% characters long.', {
                '%amount%': 256})
            }
        },
        attemptLogin() {
            this.showLoginWrongCredentialsError = false;

            this.validateEmailAndSetErrors();
            this.validatePasswordAndSetErrors();

            if (testIsNotNull(this.usernameOrEmailError) || testIsNotNull(this.passwordError)) {
                return;
            }

            globalLogger.log('user modal - login attempt - email - start');

            this.store.userPaymentModal.setSubmitting(true);

            const user = new User();

            if (this.usernameOrEmailValue.split('@').length === 2) {
                // if usernameOrEmail contains exactly one @-sign, we assume that it is an email
                user.setEmail(this.usernameOrEmailValue);
            } else {
                user.setUsername(this.usernameOrEmailValue);
            }

            user.setPassword(this.passwordValue);

            userManager
                .logInUser(user)
                .then((data) => {
                    globalLogger.log('user modal - login attempt - email - success');

                    // remove mp (modal page) from url fragments
                    window.location.hash = getLocationHashWithoutModalPageFragment();

                    window.allowLeave = true;

                    const redirectUrl = data?.redirectUrl ?? null;

                    if (testIsNonEmptyString(redirectUrl)) {
                        window.location = redirectUrl;
                    } else {
                        window.location.reload();
                    }
                })
                .catch((error) => {
                    globalLogger.log('user modal - login attempt - email - fail', error);

                    this.store.userPaymentModal.setSubmitting(false);

                    if (error === LOGIN_FAIL_NOT_ENABLED) {
                        this.$router.push({ name: userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT });

                        return;
                    }

                    if (error === LOGIN_FAIL_INVALID_DATA) {
                        this.showLoginWrongCredentialsError = true;

                        return;
                    }

                    pageNotification.showGenericErrorMessage();
                });
        },
    },
    data() {
        return {
            userModalRoute: userModalRoute,
            usernameOrEmailValue: '',
            usernameOrEmailError: null,
            passwordValue: '',
            passwordError: null,
            showLoginWrongCredentialsError: false,
            tabClosed: false,
        };
    },
};
</script>
